import {registerGlobalInitFunc} from '../modules/observer.ts';
import {GET} from '../modules/fetch.ts';

// TranscriptEvent mirrors one JSONL line of the Overwatch transcript contract.
// Every field is optional/nullable — the reader tolerates unknown and null fields.
type TranscriptEvent = {
  seq?: number;
  timestamp?: string | null;
  agentType?: string | null;
  pipelineStage?: string | null;
  operation?: string | null;
  llm?: string | null;
  prompt?: string | null;
  response?: string | null;
  thinking?: string | null;
  error?: string | null;
  durationMillis?: number | null;
};

const TAB_NAME = 'issue-overwatch-log';

function addText(parent: HTMLElement, cls: string, text: string): void {
  const el = document.createElement('div');
  el.className = cls;
  el.textContent = text; // textContent => untrusted transcript text is escaped
  parent.append(el);
}

function addBadge(parent: HTMLElement, cls: string, text: string): void {
  const el = document.createElement('span');
  el.className = `overwatch-badge ${cls}`;
  el.textContent = text;
  parent.append(el);
}

// renderTranscriptEntry builds the DOM for one transcript event. All text is set
// via textContent so untrusted inference output can never be interpreted as HTML.
export function renderTranscriptEntry(data: TranscriptEvent): HTMLElement {
  const entry = document.createElement('div');
  entry.className = 'overwatch-entry';
  if (data.error) entry.classList.add('overwatch-entry-error');

  const head = document.createElement('div');
  head.className = 'overwatch-entry-head';
  if (data.agentType) addBadge(head, 'overwatch-badge-agent', data.agentType);
  if (data.pipelineStage) addBadge(head, 'overwatch-badge-stage', data.pipelineStage);
  if (data.operation) addText(head, 'overwatch-entry-op', data.operation);
  if (typeof data.durationMillis === 'number') addText(head, 'overwatch-entry-meta', `${data.durationMillis}ms`);
  if (head.children.length) entry.append(head);

  if (data.thinking) addText(entry, 'overwatch-entry-thinking', data.thinking);
  if (data.response) addText(entry, 'overwatch-entry-response', data.response);
  if (data.error) addText(entry, 'overwatch-entry-error-msg', data.error);

  return entry;
}

// appendTranscriptLine parses one JSONL line and appends its rendered entry. A
// malformed line is skipped (resilience per the transcript contract).
export function appendTranscriptLine(content: HTMLElement, empty: HTMLElement | null, raw: string): void {
  let data: TranscriptEvent;
  try {
    data = JSON.parse(raw) as TranscriptEvent;
  } catch {
    return; // skip malformed complete line
  }
  content.append(renderTranscriptEntry(data));
  content.classList.remove('tw-hidden');
  empty?.classList.add('tw-hidden');
  content.scrollTop = content.scrollHeight;
}

// loadOverwatchLogOnce is the non-streaming fallback used when the live stream is
// unavailable (e.g. a proxy that buffers Server-Sent Events).
async function loadOverwatchLogOnce(el: HTMLElement, content: HTMLElement, empty: HTMLElement | null): Promise<void> {
  const url = el.getAttribute('data-fetch-url');
  if (!url) return;
  try {
    const resp = await GET(url);
    const data = await resp.json() as {exists: boolean; lines: string[]};
    for (const line of data.lines ?? []) appendTranscriptLine(content, empty, line);
  } catch {
    // Leave the empty state in place on failure.
  }
}

function startOverwatchLogStream(el: HTMLElement): void {
  const content = el.querySelector<HTMLElement>('.overwatch-log-content');
  const empty = el.querySelector<HTMLElement>('.overwatch-log-empty-state');
  const streamURL = el.getAttribute('data-stream-url');
  if (!content) return;

  if (!streamURL || window.EventSource === undefined) {
    loadOverwatchLogOnce(el, content, empty);
    return;
  }

  const source = new EventSource(streamURL);
  let receivedAny = false;

  source.addEventListener('transcript', (e: MessageEvent) => {
    receivedAny = true;
    appendTranscriptLine(content, empty, e.data);
  });

  source.addEventListener('error', () => {
    // A persistent failure before any data arrives: drop the stream and fall back
    // to a one-shot fetch so the pane still shows whatever is on disk.
    if (source.readyState === EventSource.CLOSED && !receivedAny) {
      loadOverwatchLogOnce(el, content, empty);
    }
  });

  window.addEventListener('beforeunload', () => source.close(), {once: true});
}

export function initOverwatchLog(el: HTMLElement): void {
  let started = false;
  const trigger = () => {
    if (started) return;
    started = true;
    startOverwatchLogStream(el);
  };

  // Lazy: only connect once the Overwatch Logs tab is actually opened. If the
  // pane is already active (restored as the active tab), connect immediately.
  if (el.closest('.ui.tab')?.classList.contains('active')) {
    trigger();
  }
  const menuItem = document.querySelector(`.item[data-tab="${TAB_NAME}"]`);
  menuItem?.addEventListener('click', trigger);
}

registerGlobalInitFunc('initOverwatchLog', initOverwatchLog);
